import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Filter, SlidersHorizontal, X, ChevronDown, ChevronUp } from 'lucide-react';
import { VehicleGrid } from '../components/VehicleCard';
import { vehicleService } from '../services/api';

export const Inventory = () => {
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    brand: '',
    model: '',
    minYear: '',
    maxYear: '',
    minPrice: '',
    maxPrice: '',
    fuelType: '',
    transmission: '',
    status: 'disponivel'
  });

  const fuelTypes = [
    { value: 'gasolina', label: 'Gasolina' },
    { value: 'alcool', label: 'Álcool' },
    { value: 'flex', label: 'Flex' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'eletrico', label: 'Elétrico' },
    { value: 'hibrido', label: 'Híbrido' }
  ];

  const transmissions = [
    { value: 'manual', label: 'Manual' },
    { value: 'automatico', label: 'Automático' },
    { value: 'cvt', label: 'CVT' },
    { value: 'semi-automatico', label: 'Semi-automático' }
  ];

  const statuses = [
    { value: 'disponivel', label: 'Disponível' },
    { value: 'vendido', label: 'Vendido' },
    { value: 'reservado', label: 'Reservado' }
  ];
  const brands = ['Toyota', 'Honda', 'Volkswagen', 'Ford', 'Chevrolet', 'Nissan', 'Hyundai', 'Fiat'];

  // Aplicar filtros vindos da URL (busca rápida da Home)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const brand = params.get('brand') || '';
    const maxPrice = params.get('maxPrice') || '';
    const fuelType = params.get('fuelType') || '';

    if (brand || maxPrice || fuelType) {
      setFilters(prev => ({
        ...prev,
        brand,
        maxPrice,
        fuelType,
      }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [location.search]);

  useEffect(() => {
    fetchVehicles();
  }, [filters, pagination.page]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: 12,
        skip: (pagination.page - 1) * 12
      };

      // Remove campos vazios
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await vehicleService.getAllVehicles(params);
      setVehicles(response.data.vehicles);
      setPagination({
        page: response.data.page,
        totalPages: response.data.totalPages,
        total: response.data.total
      });
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      brand: '',
      model: '',
      minYear: '',
      maxYear: '',
      minPrice: '',
      maxPrice: '',
      fuelType: '',
      transmission: '',
      status: 'disponivel'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeFiltersCount = Object.values(filters).filter(value => 
    value !== '' && value !== 'disponivel'
  ).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 pt-16">
      <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Estoque de Veículos
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Encontre o veículo ideal para você em nosso estoque
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="filter-section mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por marca, modelo..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-outline flex items-center space-x-2"
            >
              <SlidersHorizontal className="h-5 w-5" />
              <span>Filtros</span>
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-xs rounded-full px-2 py-1">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
              <div className="filter-grid">
                {/* Brand */}
                <div className="filter-group">
                  <label className="filter-label">Marca</label>
                  <select
                    value={filters.brand}
                    onChange={(e) => handleFilterChange('brand', e.target.value)}
                    className="select"
                  >
                    <option value="">Todas as marcas</option>
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                {/* Model */}
                <div className="filter-group">
                  <label className="filter-label">Modelo</label>
                  <input
                    type="text"
                    placeholder="Ex: Civic, Corolla..."
                    value={filters.model}
                    onChange={(e) => handleFilterChange('model', e.target.value)}
                    className="input"
                  />
                </div>

                {/* Year Range */}
                <div className="filter-group">
                  <label className="filter-label">Ano</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="De"
                      value={filters.minYear}
                      onChange={(e) => handleFilterChange('minYear', e.target.value)}
                      className="input"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                    <input
                      type="number"
                      placeholder="Até"
                      value={filters.maxYear}
                      onChange={(e) => handleFilterChange('maxYear', e.target.value)}
                      className="input"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>

                {/* Price Range */}
                <div className="filter-group">
                  <label className="filter-label">Preço</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      placeholder="Mín"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="input"
                      min="0"
                    />
                    <input
                      type="number"
                      placeholder="Máx"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="input"
                      min="0"
                    />
                  </div>
                </div>

                {/* Fuel Type */}
                <div className="filter-group">
                  <label className="filter-label">Combustível</label>
                  <select
                    value={filters.fuelType}
                    onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                    className="select"
                  >
                    <option value="">Todos</option>
                    {fuelTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Transmission */}
                <div className="filter-group">
                  <label className="filter-label">Transmissão</label>
                  <select
                    value={filters.transmission}
                    onChange={(e) => handleFilterChange('transmission', e.target.value)}
                    className="select"
                  >
                    <option value="">Todas</option>
                    {transmissions.map(trans => (
                      <option key={trans.value} value={trans.value}>{trans.label}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div className="filter-group">
                  <label className="filter-label">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="select"
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="filter-group flex items-end">
                  <button
                    onClick={clearFilters}
                    className="btn btn-secondary w-full flex items-center justify-center space-x-2"
                  >
                    <X className="h-4 w-4" />
                    <span>Limpar Filtros</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Info */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {loading ? 'Carregando...' : `${pagination.total} veículos encontrados`}
          </p>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Página {pagination.page} de {pagination.totalPages}
          </div>
        </div>

        {/* Vehicles Grid */}
        <VehicleGrid vehicles={vehicles} loading={loading} />

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn btn-outline"
              >
                Anterior
              </button>
              
              {[...Array(pagination.totalPages)].map((_, index) => {
                const page = index + 1;
                const isCurrentPage = page === pagination.page;
                const shouldShow = 
                  page === 1 || 
                  page === pagination.totalPages || 
                  Math.abs(page - pagination.page) <= 2;
                
                if (!shouldShow) {
                  if (page === 2 && pagination.page > 4) {
                    return <span key={page} className="px-3 py-2 text-gray-500">...</span>;
                  }
                  if (page === pagination.totalPages - 1 && pagination.page < pagination.totalPages - 3) {
                    return <span key={page} className="px-3 py-2 text-gray-500">...</span>;
                  }
                  return null;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`btn ${isCurrentPage ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-outline"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};